import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import useAnalyticsErrorHandler from './useAnalyticsErrorHandler';
import { generateFallbackUserAnalytics, isFallbackData } from '../utils/analyticsFallbacks';

const useAnalytics = (initialTimeRange = 'billing_period') => {
  const { getToken } = useAuth();
  const apiClient = useRef(new ApiClient(getToken)).current;
  const { error, handleError, retry: retryError, clearError, canRetry, isRetrying } = useAnalyticsErrorHandler();
  
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState(null);

  const fetchAnalytics = useCallback(async (selectedTimeRange = timeRange, customStartDate = null, customEndDate = null) => {
    setLoading(true);
    clearError();
    
    try {
      const params = new URLSearchParams({ timeRange: selectedTimeRange });
      
      if (selectedTimeRange === 'custom' && customStartDate && customEndDate) {
        params.append('startDate', customStartDate);
        params.append('endDate', customEndDate);
      }
      
      const data = await apiClient.get(`/api/analytics/dashboard?${params.toString()}`);
      
      // Extract the actual analytics data from the response
      // The response can have data in fallbackData (queued processing) or directly (cached)
      const extractedAnalytics = data.fallbackData || data;
      
      setAnalytics(extractedAnalytics);
      setLastSuccessfulFetch(new Date());
    } catch (err) {
      const displayError = await handleError(err, 'fetch_analytics', {
        timeRange: selectedTimeRange,
        customStartDate,
        customEndDate
      });
      
      // Provide fallback data if no previous successful data exists - use current analytics state
      setAnalytics(prevAnalytics => {
        if (!prevAnalytics || isFallbackData(prevAnalytics)) {
          return generateFallbackUserAnalytics(selectedTimeRange);
        }
        return prevAnalytics; // Keep existing data on error
      });
    } finally {
      setLoading(false);
    }
  }, [apiClient, timeRange, handleError, clearError]); // Removed analytics dependency!

  const retry = useCallback(async () => {
    await retryError(fetchAnalytics);
  }, [retryError, fetchAnalytics]);

  const changeTimeRange = useCallback((newTimeRange, customStartDate = null, customEndDate = null) => {
    setTimeRange(newTimeRange);
    fetchAnalytics(newTimeRange, customStartDate, customEndDate);
  }, [fetchAnalytics]);

  // Initial load
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Auto-refresh every 5 minutes for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !error && !isRetrying) {
        fetchAnalytics();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [fetchAnalytics, loading, error, isRetrying]);

  return {
    analytics,
    loading,
    error,
    timeRange,
    retry,
    changeTimeRange,
    refresh: fetchAnalytics,
    canRetry,
    isRetrying,
    lastSuccessfulFetch,
    isFallbackData: analytics ? isFallbackData(analytics) : false
  };
};

export default useAnalytics;
